import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { QueryVariables, Records } from 'src/graphql/types';

const GET_PARTNER_PRODUCTS_QUERY = gql`
  query GetPartnerProduct($params: QueryParams) @entity(name: "PartnerProduct") {
    getPartnerProduct(queryParams: $params) {
      records {
        id
        partnerProductId
        displayName
        platformSlaId
        stripeProductId
        callForPricing
        prices {
          id
          partnerProductPriceId
          stripePriceId
          amountCents
          intervalUnit
          isDefault
          isActive
        }
      }
    }
  }
`;

type PartnerProductPrice = {
  partnerProductPriceId: string;
  stripePriceId: string;
  amountCents: number;
  intervalUnit: string;
  isDefault: boolean;
  isActive: boolean;
};

type PartnerProductRecord = {
  partnerProductId: string;
  displayName: string;
  platformSlaId?: string | null;
  stripeProductId?: string | null;
  callForPricing: boolean;
  prices?: PartnerProductPrice[] | null;
};

export type PartnerProductPriceSummary = {
  id: string;
  stripePriceId: string;
  amountCents: number;
};

export type PartnerProductSummary = {
  partnerProductId: string;
  displayName: string;
  platformSlaId: string | null;
  stripeProductId: string | null;
  callForPricing: boolean;
  prices: {
    monthly: PartnerProductPriceSummary | null;
    annual: PartnerProductPriceSummary | null;
  };
};

type GetPartnerProductData = {
  getPartnerProduct: Records<PartnerProductRecord>;
};

const pickPriceByInterval = (
  prices: PartnerProductPrice[],
  intervalUnit: 'month' | 'year',
): PartnerProductPriceSummary | null => {
  const activePrices = prices.filter((price) => price.isActive);
  const match =
    activePrices.find((price) => price.intervalUnit === intervalUnit && price.isDefault) ??
    activePrices.find((price) => price.intervalUnit === intervalUnit);

  if (!match) {
    return null;
  }

  return {
    id: match.partnerProductPriceId,
    stripePriceId: match.stripePriceId,
    amountCents: match.amountCents,
  };
};

export const useMembershipsPartnerProducts = (
  params: {
    partnerId: number | null;
  },
  options?: QueryHookOptions<GetPartnerProductData>,
) => {
  const { partnerId } = params ?? {};
  const { data, ...rest } = useQuery<GetPartnerProductData, QueryVariables>(
    GET_PARTNER_PRODUCTS_QUERY,
    {
      ...options,
      skip: isNullish(partnerId) || options?.skip,
      variables: {
        params: {
          filters: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: String(partnerId),
            },
            {
              attribute: 'isActive',
              operator: '=',
              value: 'true',
            },
          ],
        },
      },
    },
  );

  const partnerProducts = useMemo<PartnerProductSummary[]>(() => {
    return (data?.getPartnerProduct.records ?? []).map((product) => {
      const prices = Array.isArray(product.prices) ? product.prices : [];

      return {
        partnerProductId: product.partnerProductId,
        displayName: product.displayName,
        platformSlaId: product.platformSlaId ?? null,
        stripeProductId: product.stripeProductId ?? null,
        callForPricing: product.callForPricing ?? false,
        prices: {
          monthly: pickPriceByInterval(prices, 'month'),
          annual: pickPriceByInterval(prices, 'year'),
        },
      };
    });
  }, [data]);

  return {
    partnerProducts,
    ...rest,
  };
};
